import React, { useMemo } from 'react';

import Icon from '@/common/components/Icon';
import Timing from '@/common/components/Timing';
import { useNavigateWithLanguage } from '@/common/hooks/useNavigateWithLanguage';
import { PATHS } from '@/sites/op7/routes/paths';

import styles from './headerMobile.module.scss';
import LazyImage from '@/common/components/LazyImage';
import clsx from 'clsx';

import type { MatchRecord } from '@/apis/fbSports/getList';
import { formatFBSportItem, getSpecialSportScore } from '@/apis/fbSports/common/fbFormat';

interface HeaderMobileProps {
  match: MatchRecord;
  isFavorite: boolean;
  isMatchTeamHeader: boolean;
  isVideoVisible: boolean;
  onBack: () => void;
  onToggleFavorite: () => void;
  onDrawerOpen?: () => void;
}

/**
 * 赛事详情页头部组件
 */
const HeaderMobile: React.FC<HeaderMobileProps> = ({
  match,
  isFavorite,
  isMatchTeamHeader,
  isVideoVisible,
  onBack,
  onToggleFavorite,
  onDrawerOpen,
}) => {
  const navigate = useNavigateWithLanguage();

  const formattedMatch = useMemo(() => {
    const base = formatFBSportItem(match);

    return { ...base, matchPeriod: base.periodName ?? base.matchPeriod ?? '' };
  }, [match]);

  const {
    homeName,
    homeLogo,
    awayName,
    awayLogo,
    homeScore,
    awayScore,
    matchDate,
    isLive,
    matchPeriod,
    isCountdown,
    matchTime,
    leagueName,
    clockType,
    sportId,
  } = formattedMatch;

  const showTeamHeader = useMemo(() => {
    return isVideoVisible || isMatchTeamHeader;
  }, [isVideoVisible, isMatchTeamHeader]);

  return (
    <div className={clsx(styles.headerMobile, showTeamHeader ? styles.scrolled : '')}>
      <div className={styles.left}>
        <button className={styles.backButton} onClick={onBack} aria-label="返回">
          {isVideoVisible ? (
            <Icon src="/images/common/close.svg" size="24px" color="var(--Text-Main-10)" />
          ) : (
            <Icon
              src="/images/common/back.svg"
              size="16px"
              color={showTeamHeader ? 'var(--Text-Main-10)' : 'var(--White-100)'}
            />
          )}
        </button>
      </div>

      <div className={styles.center} onClick={onDrawerOpen}>
        <div className={clsx(styles.league, showTeamHeader ? styles.hide : '')}>
          <span className={`${styles.leagueName} _tf[16]`}>{leagueName}</span>
          <Icon src="/images/common/arrow_down.svg" size="12px" color="var(--White-100)" />
        </div>

        <div className={clsx(styles.match, showTeamHeader ? styles.show : '')}>
          <div className={clsx(styles.team, 'justify-end')}>
            <span className={`${styles.label} _tf[12]`}>{homeName}</span>
            <LazyImage src={homeLogo} width={16} height={16} className={styles.teamLogo} />
          </div>
          <div className={styles.info}>
            <div>
              <span className="_tf[10]">{isLive ? matchPeriod : matchDate}</span>
              {isLive && matchTime !== 0 && (
                <span className={styles.matchTime}>
                  <Timing
                    className="_tf[10]"
                    time={matchTime}
                    running={isCountdown}
                    isCountdown={clockType === 'DESC'}
                  />
                </span>
              )}
            </div>
            <div className="_tf[12] font-din-pro">
              {isLive
                ? `${getSpecialSportScore(sportId, homeScore)} - ${getSpecialSportScore(sportId, awayScore)}`
                : 'VS'}
            </div>
          </div>
          <div className={clsx(styles.team, 'justify-start')}>
            <LazyImage src={awayLogo} width={16} height={16} className={styles.teamLogo} />
            <span className={`${styles.label} _tf[12]`}>{awayName}</span>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        <button
          className={`${styles.iconButton} ${isFavorite ? styles.active : ''}`}
          onClick={onToggleFavorite}
          aria-label={isFavorite ? '取消收藏' : '收藏'}
        >
          {isFavorite ? (
            <img src="/images/common/favorite_sel.png" width={20} alt="" />
          ) : (
            <Icon
              src="/images/common/followed_h5.svg"
              size={20}
              color={
                isFavorite
                  ? 'var(--Warning-200)'
                  : showTeamHeader
                    ? 'var(--Text-Main-10)'
                    : 'var(--White-100)'
              }
            />
          )}
        </button>
        <button
          className={styles.iconButton}
          aria-label="注单"
          onClick={() => navigate(PATHS.betHistoryH5)}
        >
          <Icon
            src="/images/common/record_h5.svg"
            size={20}
            color={showTeamHeader ? 'var(--Text-Main-10)' : 'var(--White-100)'}
          />
        </button>
      </div>
    </div>
  );
};

export default HeaderMobile;
